import React, { MouseEventHandler, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import StatusBadge from './StatusBadge';
import Person from '@mui/icons-material/Person';
import FALLBACK_SVG from '../../../images/missing-image-person.svg';
import Notification from './Notification';
import { useInView } from 'react-intersection-observer';
import { AvatarSize } from './ChatAvatar';
import { useImageCache } from '../../hooks/useImageCache';

interface Props {
    className?: string;
    src?: string;
    children?: React.ReactNode;
    status?:
        | 'available'
        | 'ringingCall'
        | 'inCall'
        | 'inExternalCall'
        | 'unavailable'
        | 'unknown';
    count?: number;
    onClick?: MouseEventHandler;
    lazyLoad?: boolean;
    size?: AvatarSize;
}

const sizes = {
    tiny: 'size-4 text-[8px]',
    extraSmall: 'size-5 text-2xs',
    small: 'size-6 text-2xs',
    medium: 'size-9',
    toolbar: 'size-10',
    large: 'size-14 text-lg',
    extraLarge: 'size-20 text-2xl',
    videoLarge: 'size-40 text-3xl',
    // Scales with the meeting tile it sits on, initials included, rather than
    // picking a fixed step — see the meeting-tile-avatar utility.
    meetingTile: 'meeting-tile-avatar',
};

const UserAvatar = ({
    className = '',
    src,
    children = <Person />,
    status,
    count,
    onClick,
    lazyLoad = false,
    size = 'toolbar',
}: Props) => {
    const [ref, inView] = useInView({
        triggerOnce: true,
        rootMargin: '100px 0px',
    });

    // Use cached image when lazyLoad is enabled and image is in view, or when lazyLoad is disabled
    const shouldLoadImage = !lazyLoad || inView;
    const { src: cachedSrc, loading: cacheLoading } = useImageCache(
        src,
        shouldLoadImage,
    );

    const [loading, setLoading] = useState(true);

    const animate = ['ringingCall', 'inCall', 'inExternalCall'].includes(
        status,
    );

    const img = (
        <img
            className={twMerge(
                'rounded-full object-cover',
                sizes[size],
                className,
                loading || cacheLoading ? 'bg-gray-200' : '',
            )}
            src={cachedSrc}
            // Decorative: every list row renders the person's name beside the
            // avatar, so naming it here would announce the name twice.
            alt=""
            onLoad={() => setLoading(false)}
            onError={(e) => {
                setLoading(false);
                e.currentTarget.onerror = null;
                e.currentTarget.src = FALLBACK_SVG;
            }}
        />
    );

    // Determine what to render based on cache state and lazy loading
    const renderContent = () => {
        if (!cachedSrc) {
            return children;
        }
        if (lazyLoad && !inView) {
            return children; // Show placeholder while lazy loading
        }
        return img;
    };

    return (
        <div className="relative inline-block text-xs">
            <div
                className={twMerge(
                    'relative flex items-center justify-center rounded-full font-bold text-white',
                    // leading-none so initials centre vertically — without it
                    // the inherited line-height (taller than the font on small
                    // sizes) pushes the glyph above centre.
                    'leading-none',
                    className,
                    sizes[size],
                    onClick ? 'cursor-pointer' : '',
                )}
                onClick={onClick}
                ref={ref}
            >
                {renderContent()}
            </div>

            {count ? (
                <Notification className="-me-1">{count}</Notification>
            ) : null}

            {status && status !== 'unknown' ? (
                <StatusBadge status={status} size={size} animate={animate} />
            ) : null}
        </div>
    );
};

export default UserAvatar;
